import axios from "axios";
import semverCoerce from "semver/functions/coerce";
import semverSatisfies from "semver/functions/satisfies";

const handleErrorToStatus = (error) => {
  if (error.response && error.response.data && error.response.data.code) {
    if (error.response.data.code === 400) return { status: "notFound" };
    if (error.response.data.code === 404) return { status: "notFound" };
    if (error.response.data.code === 500) return { status: "error" };
  }
  // it's not a response from dido api server
  console.log(error);
  return { status: "error" };
};

const getFullUrlPagination = (url, type, params) => {
  let queryString = Object.entries(params)
    .map(([key, value]) => `${key}=${value}`)
    .join("&");
  return `${url}/${type}s?${queryString}`;
};
const getFullUrlFacets = (url, type, params) => {
  let queryString = Object.entries(params)
    .map(([key, value]) => `${key}=${value}`)
    .join("&");
  return `${url}/${type}s/facets?${queryString}`;
};
const refsTitles = {
  "referentiels/ports": "Référentiels des ports",
  "referentiels/polluantsEau": "Référentiel des polluants des eaux",
  "referentiels/stationsAir": "Référentiel des stations d'air",
  "referentiels/stationsEsu": "Référentiel des stations des eaux de surface",
  "referentiels/cog": "Référentiel du code officiel géographique",
  "referentiels/geozones": "Référentiel des géozones",
  "referentiels/naf": "Référentiel de la NAF",
  "nomenclatures/bilanEnergie": "Nomenclature du bilan de l'énergie",
  "nomenclatures/cslFilieres":
    "Nomenclature des filières des comptes satellites du logement",
  "nomenclatures/cslOperations":
    "Nomenclature des opérations des comptes satellites du logement",
};
const getApiVersion = (url) =>
  axios
    .get(`${url}/../appVersion`)
    .then((response) => `v${response.data.version}`)
    .catch(() => "inconnue");

export default {
  withParquet: (url) =>
    getApiVersion(url).then((version) =>
      semverSatisfies(semverCoerce(version), ">=1.8.0"),
    ),
  getApiVersion,
  getDatasets: (url, params) =>
    new Promise((resolve) => {
      axios
        .get(getFullUrlPagination(url, "dataset", params))
        .then((response) => {
          let status = "";
          if (response.status === 200) status = "available";
          return resolve({ status, pagination: response.data });
        })
        .catch((error) => resolve(handleErrorToStatus(error)));
    }),
  facetsDatasets: (url, params) =>
    new Promise((resolve) => {
      axios
        .get(getFullUrlFacets(url, "dataset", params))
        .then((response) => {
          let status = "";
          if (response.status === 200) status = "available";
          return resolve({ status, facets: response.data });
        })
        .catch((error) => resolve(handleErrorToStatus(error)));
    }),
  getDatafiles: (url, params) =>
    new Promise((resolve) => {
      axios
        .get(getFullUrlPagination(url, "datafile", params))
        .then((response) => {
          let status = "";
          if (response.status === 200) status = "available";
          return resolve({ status, pagination: response.data });
        })
        .catch((error) => resolve(handleErrorToStatus(error)));
    }),
  facetsDatafiles: (url, params) =>
    new Promise((resolve) => {
      axios
        .get(getFullUrlFacets(url, "datafile", params))
        .then((response) => {
          let status = "";
          if (response.status === 200) status = "available";
          return resolve({ status, facets: response.data });
        })
        .catch((error) => resolve(handleErrorToStatus(error)));
    }),
  getDataset: (url, id) =>
    new Promise((resolve) => {
      axios
        .get(`${url}/datasets/${id}`)
        .then((response) => {
          let status = "";
          if (response.status === 200) status = "available";
          return resolve({ status, dataset: response.data });
        })
        .catch((error) => resolve(handleErrorToStatus(error)));
    }),
  getDatafile: (url, rid) => {
    let datafile = null;
    return new Promise((resolve) => {
      axios
        .get(`${url}/datafiles/${rid}`)
        .then((response) => {
          if ([200, 206].includes(response.status)) {
            datafile = response.data;
            return datafile.dataset.id;
          }
          throw new Error();
        })
        .then((datasetId) => axios.get(`${url}/datasets/${datasetId}`))
        .then((response) => {
          if (response.status === 200) {
            datafile.millesimes = response.data.datafiles.find(
              (d) => d.rid === rid,
            ).millesimes;
            return resolve({ status: "available", datafile });
          }
          throw new Error();
        })
        .catch((error) => resolve(handleErrorToStatus(error)));
    });
  },
  getMillesime: (url, rid, millesime) =>
    new Promise((resolve) => {
      axios
        .get(`${url}/datafiles/${rid}?millesime=${millesime}`)
        .then((response) => {
          let status = "";
          if (response.status === 200) status = "available";
          return resolve({ status, millesimeObject: response.data });
        })
        .catch((error) => resolve(handleErrorToStatus(error)));
    }),
  getMillesimeInfos: (url, rid, millesime) =>
    new Promise((resolve) => {
      axios
        .get(`${url}/datafiles/${rid}?millesime=${millesime}`)
        .then((response) => {
          let status = "";
          if (response.status === 200) status = "available";
          if (response.status === 206) status = "delayed";
          return resolve({ status, infos: response.data });
        })
        .catch((error) => resolve(handleErrorToStatus(error)));
    }),
  getGeozone(url, id) {
    return axios.get(`${url}/referentiels/geozones/${id}`);
  },
  getTag(url, id) {
    return axios.get(`${url}/referentiels/tags/${id}`);
  },
  frequencies: [
    { id: "daily", label: "Quotidienne" },
    { id: "weekly", label: "Hebdomadaire" },
    { id: "monthly", label: "Mensuelle" },
    { id: "quarterly", label: "Trimestrielle" },
    { id: "semiannual", label: "Semestrielle" },
    { id: "annual", label: "Annuelle" },
    { id: "punctual", label: "Ponctuelle" },
    { id: "irregular", label: "Sans régularité" },
    { id: "unknown", label: "Inconnue" },
  ],
  granularities: [
    { id: "country", label: "France" },
    { id: "fr:region", label: "Région" },
    { id: "fr:epci", label: "Intercommunalité (EPCI)" },
    { id: "fr:departement", label: "Département" },
    { id: "fr:collectivite", label: "Collectivité d'outre-mer" },
    { id: "fr:commune", label: "Commune" },
    { id: "fr:arrondissement", label: "Arrondissement" },
    { id: "fr:canton", label: "Canton" },
    { id: "fr:iris", label: "Iris (quartiers INSEE)" },
    { id: "poi", label: "Point d'intérêt" },
    { id: "other", label: "Autre" },
  ],
  refTitle: (text) => {
    const refName =
      text.indexOf(" ") > -1 ? text.substr(0, text.indexOf(" ")) : text;
    return text.replace(refName, refsTitles[refName]);
  },
  refs: (url, name, filters = [], labelFilters = []) => {
    const queryFilters = filters.length
      ? `&${filters
          .map(({ field, filter, value }) => `${field}=${filter}:${value}`)
          .join("&")}`
      : "";
    const titleFilters = labelFilters.length
      ? ` (limité à ${labelFilters.join(" et ")})`
      : "";
    return {
      title: `${refsTitles[name]}${titleFilters}`,
      url: `${url}/${name}/csv?withColumnName=true&withColumnDescription=true${queryFilters}`,
    };
  },
  urlDatafileBase: (url, rid) => `${url}/datafiles/${rid}`,
  getNameAndDataFromLink: async (link) =>
    axios.get(link, { responseType: "blob" }).then((response) => ({
      data: response.data,
      name: response.headers["content-disposition"].match(
        /.*filename="(.*)"/,
      )[1],
    })),
  urlMillesimeSwagger: (url, rid, millesime) =>
    `${url}/datafiles/${rid}/swagger.json?millesime=${millesime}`,
  rowPageSizeOptions: ["10", "20", "50", "100"],
  filters: {
    eq: "égal à",
    ne: "différent de",
    gt: "supérieur à",
    gte: "supérieur ou égal à",
    lt: "inférieur à",
    lte: "inférieur ou égal à",
    in: "appartient à",
    nin: "n'appartient pas",
    startsWith: "commence par",
    contains: "contient",
    endsWith: "finit par",
    withinCogZones: "à l'intérieur d'une zone administrative",
    withinGeometry: "à l'intérieur d'une géométrie",
  },
  getDownloadUrl: (
    url,
    rid,
    millesime,
    { format, page, pageSize, geoField, headers, sort, filters, columns },
  ) => {
    const paginationQuery = ["rows"].includes(format)
      ? `&page=${page}&pageSize=${pageSize}`
      : "";
    const headersQuery = ["csv", "xlsx"].includes(format)
      ? `&withColumnName=${headers.withColumnName}&withColumnDescription=${headers.withColumnDescription}&withColumnUnit=${headers.withColumnUnit}`
      : "";
    const geoFieldQuery = ["spatial/geojson"].includes(format)
      ? `&geoField=${geoField}`
      : "";
    const sortQuery = sort.length
      ? `&orderBy=${sort
          .map((sortClause) =>
            sortClause.order === "desc"
              ? `-${sortClause.field}`
              : sortClause.field,
          )
          .join(",")}`
      : "";
    const filtersQuery = filters.length
      ? `&${filters
          .map(
            ({ field, name, value }) =>
              `${field}=${name}:${encodeURIComponent(value)}`,
          )
          .join("&")}`
      : "";
    const columnsQuery = columns.length ? `&columns=${columns.join(",")}` : "";
    return `${url}/datafiles/${rid}/${format}?millesime=${millesime}${paginationQuery}${geoFieldQuery}${headersQuery}${sortQuery}${filtersQuery}${columnsQuery}`;
  },
  getPage: async (
    url,
    rid,
    millesime,
    { page, pageSize, sort, filters, columns },
  ) => {
    const sortQuery = sort ? `&orderBy=${sort}` : "";
    let filtersQuery = "";
    Object.entries(filters).forEach(([colName, filter]) => {
      filtersQuery += `&${colName}=${filter.type}:${filter.value}`;
    });
    const columnsQuery = columns.length ? `&columns=${columns.join(",")}` : "";

    const result = await axios
      .get(
        `${url}/datafiles/${rid}/page?millesime=${millesime}&page=${page}&pageSize=${pageSize}${sortQuery}${filtersQuery}${columnsQuery}`,
      )
      .catch((error) => {
        const message = error?.response?.data?.message
          ? error.response.data.message
          : error.message;
        throw new Error(message);
      });
    return result.data?.page.map((item, index) => ({ ...item, index }));
  },
  countRows: async (url, rid, millesime, filters) => {
    let filtersQuery = "";
    Object.entries(filters).forEach(([colName, filter]) => {
      filtersQuery += `&${colName}=${filter.type}:${filter.value}`;
    });
    const result = await axios
      .get(
        `${url}/datafiles/${rid}/count?millesime=${millesime}${filtersQuery}`,
      )
      .catch((error) => {
        const message = error?.response?.data?.message
          ? error.response.data.message
          : error.message;
        throw new Error(message);
      });
    return result.data?.total;
  },
  cogOptions: {
    millesimes: [2015, 2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023].map(
      (val) => ({
        label: String(val),
        value: val,
      }),
    ),
    levels: [
      { label: "Région", value: "region" },
      { label: "Département", value: "departement" },
      {
        label: "Arrondissement départemental",
        value: "arrondissement-departemental",
      },
      { label: "Canton", value: "canton" },
      { label: "EPCI", value: "epci" },
      { label: "EPT", value: "ept" },
      { label: "Commune", value: "commune" },
      { label: "IRIS", value: "iris" },
    ],
  },
  getCogZones: async (url, millesimeCog, levelCog, searchTerm) =>
    new Promise((resolve, reject) => {
      axios
        .get(
          `${url}/referentiels/cog?page=1&pageSize=20&orderBy=UPPER_NAME&columns=ID,OFFICIAL_NAME&MILLESIME=eq:${millesimeCog}&LEVEL=eq:${levelCog}&UPPER_NAME=startsWith:${searchTerm}`,
        )
        .then((response) => {
          resolve(
            response.data.data.map(({ ID, OFFICIAL_NAME }) => ({
              label: OFFICIAL_NAME,
              value: ID,
            })),
          );
        })
        .catch((error) => reject(error));
    }),
  getCogZone: async (url, id) =>
    new Promise((resolve) => {
      axios
        .get(`${url}/referentiels/cog/json?ID=eq:${id}`)
        .then((response) => {
          if (!response.data) resolve(null);
          if (response.data.length !== 1) resolve(null);
          resolve(response.data[0]);
        })
        .catch(() => resolve(null));
    }),
  exportDcatUrl: (url, format) =>
    `${url}/../harvesting/dcat-ap/export/${format}`,
  urlGlobalSwagger: (url) => `${url}/swagger.json`,
  getDatasetLinkedDocs: (url, id) =>
    new Promise((resolve) => {
      axios
        .get(url)
        .catch(() => ({ data: [] }))
        .then((response) => {
          resolve(
            response.data
              // get only docs of type Dataset and with corresponding id
              .filter(
                ({ type_dido, datasetid }) =>
                  type_dido === "dataset" && datasetid === id,
              )
              // get an unique array of docs (the id is the attribute nid)
              .reduce(
                (uniqueDocs, currentDoc) =>
                  uniqueDocs.find(({ nid }) => currentDoc.nid === nid)
                    ? uniqueDocs
                    : [...uniqueDocs, currentDoc],
                [],
              )
              // map the linkedDocs attributes to our client model
              .map(
                ({
                  content_title,
                  content_summary,
                  content_link,
                  created,
                }) => ({
                  title: content_title,
                  url: content_link,
                  description: content_summary,
                  created,
                }),
              )
              // sort by creation date
              .sort((s1, s2) => (s1.created < s2.created ? 1 : -1)),
          );
        })
        .catch(() => resolve([]));
    }),
  getDatafileLinkedDocs: (url, rid) =>
    new Promise((resolve) => {
      axios
        .get(url)
        .catch(() => ({ data: [] }))
        .then((response) => {
          resolve(
            response.data
              // get only docs of type Datafile and with corresponding rid
              .filter(
                ({ type_dido, datafileRid }) =>
                  type_dido === "datafile" && datafileRid === rid,
              )
              // get an unique array of docs (the id is the attribute nid)
              .reduce(
                (uniqueDocs, currentDoc) =>
                  uniqueDocs.find(({ nid }) => currentDoc.nid === nid)
                    ? uniqueDocs
                    : [...uniqueDocs, currentDoc],
                [],
              )
              // map the linkedDocs attributes to our client model
              .map(
                ({
                  content_title,
                  content_summary,
                  content_link,
                  created,
                }) => ({
                  title: content_title,
                  url: content_link,
                  description: content_summary,
                  created,
                }),
              )
              // sort by creation date
              .sort((s1, s2) => (s1.created < s2.created ? 1 : -1)),
          );
        })
        .catch(() => resolve([]));
    }),
};
